(function initializeFirstOrderOffer(root, createApi) {
  'use strict';
  const api = createApi(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root?.customElements && !root.customElements.get('alvaura-first-order-offer')) {
    root.customElements.define('alvaura-first-order-offer', api.AlvauraFirstOrderOffer);
  }
})(typeof window === 'object' ? window : null, function createFirstOrderOfferApi(root) {
  'use strict';
  const keys = [
    'ruleVerified', 'ruleCode', 'ruleAmount', 'ruleMinimum', 'currency', 'priceCents',
    'variantId', 'productId', 'blockId', 'sectionId', 'formId', 'available',
    'customerStatus', 'oneTime', 'quantityMin'
  ];
  const yes = (value) => value === true || value === 'true';
  const no = (value) => value === false || value === 'false';
  const id = (value) => typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;
  const integer = (value) => {
    if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value))) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= 0 ? number : null;
  };
  const state = (showBadge, showEstimate, reason, estimateCents = null) => ({ showBadge, showEstimate, reason, estimateCents });

  function evaluate(input = {}) {
    if (!yes(input.ruleVerified) || input.ruleCode !== 'Welcome20' ||
      integer(input.ruleAmount) !== 2000 || integer(input.ruleMinimum) !== 15000) {
      return state(false, false, 'unverified-rule');
    }
    if (input.customerStatus === 'returning') return state(false, false, 'returning-customer');
    if (no(input.available)) return state(false, false, 'unavailable-variant');
    if (no(input.oneTime) || (typeof input.sellingPlan === 'string' && input.sellingPlan !== '')) {
      return state(false, false, 'subscription');
    }
    if (!['guest', 'new'].includes(input.customerStatus) || !yes(input.available) || !yes(input.oneTime)) {
      return state(true, false, 'unknown-eligibility');
    }
    if (input.pending === true) return state(true, false, 'pending-variant');
    if (!yes(input.formKnown) || !id(input.variantId) || input.formVariantId !== input.variantId ||
      input.sellingPlan !== '') return state(true, false, 'unresolved-form');
    if (input.currency !== 'USD') return state(true, false, 'other-currency');
    if (integer(input.quantity) !== 1) return state(true, false, 'quantity-not-one');
    if (integer(input.quantityMin) !== 1) return state(true, false, 'single-item-not-permitted');
    const price = integer(input.priceCents);
    if (price === null) return state(true, false, 'unknown-price');
    if (price < 15000) return state(true, false, 'below-minimum');
    return state(true, true, 'conditional-estimate', price - 2000);
  }

  class AlvauraFirstOrderOffer extends (root?.HTMLElement || class {}) {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.pending = false;
      this.sourceValid = true;
      this.priceHost = this.closest('product-price.alvaura-first-order-price');
      this.productInfo = this.closest('product-info');
      this.document = this.ownerDocument;
      this.variantEvent = root?.MainEvents?.variantUpdate || 'variant:update';
      this.onInput = (event) => {
        const form = this.mainForm();
        const target = event.target;
        const isVariantSelection = Boolean(this.productInfo?.contains(target) && target?.closest?.('variant-selects'));
        if (isVariantSelection) {
          this.pending = true;
          this.refresh();
        }
        if (isVariantSelection || (form && (target?.form === form || form.contains(target)))) this.schedule();
      };
      this.onVariantUpdate = (event) => this.syncVariant(event);
      this.document?.addEventListener('input', this.onInput);
      this.document?.addEventListener('change', this.onInput);
      this.document?.addEventListener(this.variantEvent, this.onVariantUpdate);
      this.refresh();
    }

    disconnectedCallback() {
      this.connected = false;
      this.document?.removeEventListener('input', this.onInput);
      this.document?.removeEventListener('change', this.onInput);
      this.document?.removeEventListener(this.variantEvent, this.onVariantUpdate);
      if (this.priceHost) delete this.priceHost.dataset.firstOrderEstimate;
    }

    mainForm() {
      const data = this.dataset;
      if (!data.sectionId || data.formId !== `product-form-${data.sectionId}` ||
        !this.productInfo || this.productInfo.id !== `MainProduct-${data.sectionId}` ||
        this.productInfo.dataset.section !== data.sectionId ||
        this.productInfo.dataset.productId !== data.productId || !id(data.productId)) return null;
      const matches = [...(this.document?.querySelectorAll('form[id]') || [])].filter((form) => form.getAttribute('id') === data.formId);
      if (matches.length !== 1 || !this.productInfo.contains(matches[0])) return null;
      return matches[0];
    }

    readForm() {
      const form = this.mainForm();
      if (!form?.elements?.namedItem) return { formKnown: false };
      const control = (name) => {
        const field = form.elements.namedItem(name);
        return field && typeof field.value === 'string' && !field.disabled &&
          field.validity?.valid !== false && ['INPUT', 'SELECT'].includes(field.tagName) &&
          (!field.form || field.form === form) ? field : null;
      };
      const variant = control('id');
      const quantity = control('quantity');
      const plan = control('selling_plan');
      const planField = form.elements.namedItem('selling_plan');
      const selectedPlan = typeof planField?.value === 'string' && planField.value !== '' ? planField.value : null;
      const missingPlan = planField == null;
      const missingQuantity = form.elements.namedItem('quantity') == null;
      return {
        formKnown: true,
        formVariantId: variant?.value,
        quantity: quantity?.value ?? (missingQuantity && integer(this.dataset.quantityMin) === 1 ? 1 : null),
        sellingPlan: selectedPlan ?? plan?.value ?? (missingPlan && yes(this.dataset.oneTime) ? '' : null)
      };
    }

    refresh() {
      if (!this.connected) return;
      const result = evaluate({ ...this.dataset, ...this.readForm(), pending: this.pending });
      const estimate = this.querySelector('[data-first-order-estimate]');
      const badge = this.querySelector('[data-first-order-badge]');
      const showBadge = result.showBadge && this.sourceValid;
      const showEstimate = showBadge && result.showEstimate && Boolean(estimate && this.priceHost);
      this.hidden = !showBadge;
      if (estimate) estimate.hidden = !showEstimate;
      if (badge) badge.hidden = !showBadge;
      if (this.priceHost) {
        if (showEstimate) this.priceHost.dataset.firstOrderEstimate = 'true';
        else delete this.priceHost.dataset.firstOrderEstimate;
      }
      return result;
    }

    schedule() {
      if (this.queued) return;
      this.queued = true;
      const enqueue = root?.queueMicrotask || globalThis.queueMicrotask;
      enqueue.call(root || globalThis, () => {
        this.queued = false;
        if (this.connected) this.refresh();
      });
    }

    syncVariant(event) {
      const detail = event?.detail;
      const data = detail?.data;
      if (String(data?.productId || '') !== this.dataset.productId ||
        ![this.dataset.sectionId, this.productInfo?.id].includes(detail?.sourceId)) return;
      this.pending = true;
      this.refresh();
      const variantId = detail?.resource?.id == null ? null : String(detail.resource.id);
      const candidates = [...(data?.html?.querySelectorAll?.('alvaura-first-order-offer') || [])].filter((node) =>
        ['productId', 'sectionId', 'blockId', 'formId'].every((key) => node.dataset?.[key] === this.dataset[key]));
      if (!id(variantId) || candidates.length !== 1 || candidates[0].dataset.variantId !== variantId ||
        this.readForm().formVariantId !== variantId) {
        this.sourceValid = false;
        this.refresh();
        return;
      }
      const incoming = candidates[0];
      for (const key of keys) {
        if (incoming.dataset[key] === undefined) delete this.dataset[key];
        else this.dataset[key] = incoming.dataset[key];
      }
      this.innerHTML = incoming.innerHTML;
      this.sourceValid = true;
      this.pending = false;
      this.schedule();
    }
  }
  return { evaluate, AlvauraFirstOrderOffer };
});
